import React, { useEffect, useState } from "react";
import BaseTable from "../../Components/Common/BaseTable"; 
import BaseButton from "../../Components/Common/BaseButton"; 
import Icon from '@mdi/react';
import { mdiPlus } from '@mdi/js';
import "../../STYLE/ListOfProducts.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { REQUEST_DELETE_MEMBERS_COMMON, REQUEST_LIST_OF_MEMBERS_COMMON } from "API/Services/common"; 
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import {ACTION_HEADER, ACTIONS_ACCESSOR, ADD_LABEL, ARE_YOU_SURE_DELETE, CANCEL_LABEL, DELETE_CAPI, DELETE_LABEL, EDIT_CAPI, ID_ACCESSOR, MEMBERS, PRODUCTS_TITLE, SEARCH_PRODUCTS, SERIAL_NO } from "Components/constants/commonNames";
import { checkStatusCodeSuccess } from "Components/constants/enums";
import { BaseImageURL } from "API/Services/services";

interface Member {
  id: number;
  profile_image: string;
  name: string;
  position: string;
}

interface PaginationInfo {
  totalPages: number;
  totalRecordsCount: number;
  currentPage: number;
  numberOfRows: any | number ;
}

const ListOfMembers = () => {
    const [members, setMembers] = useState<Member[]>([]);
    const [modal, setModal] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [columnName, setColumnName] = useState("id");
    const [sortOrder, setSortOrder] = useState("desc");
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
      totalPages: 1,
      totalRecordsCount: 0,
      currentPage: 1,
      numberOfRows: 10, 
    });
    const [loader, setLoader] = useState(false);
    const [fullScreenLoader, setFullScreenLoader] = useState(false);
  
    const navigate = useNavigate();


    const columns = [
      {
        enableColumnFilter: false,
        accessorKey: ID_ACCESSOR,
        header: SERIAL_NO,
        cell: ({ row }: any) => row.index + 1,
      },
      {
        enableColumnFilter: false,
        accessorKey: "profile_image",
        header: "Profile Image",
        cell: ({ row }: any) => (
          <img 
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src="https://lightwidget.com/wp-content/uploads/localhost-file-not-found.jpg";
          }}
            src={`${BaseImageURL}${row.original.profile_image}`} 
            alt={row.original.name}
            width="40" 
            height="40"
          />
        )
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "position",
        header: "Position",
      },
      {
        id: ACTIONS_ACCESSOR,
        header: ACTION_HEADER,
        cell: ({ row }: any) => (
          <div className="d-flex">
            <i
              className="text-info mdi mdi-pencil fs-4 cursor-pointer"
              onClick={() => navigate(`/edit-member/${row.original.id}`)}
              title={EDIT_CAPI}
            />
            <i
              className="text-danger mdi mdi-delete fs-4 cursor-pointer"
              onClick={() => confirmDelete(row.original)}
              title={DELETE_CAPI}
            />
          </div>
        ),
      },
    ];
  
    const payload = {
      searchBar: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
      pageSize: pageSize,
      page: page,
    };
  
    const fetchMembers = async () => {
      setFullScreenLoader(true);
      try {
        const response = await REQUEST_LIST_OF_MEMBERS_COMMON(payload);
  
        if (checkStatusCodeSuccess(response?.statusCode)) {
          setMembers(response.data.membersData); 
  
          setPaginationInfo({
            totalPages: response.data.totalPages,
            totalRecordsCount: response.data.totalRecordsCount,
            currentPage: response.data.currentPage,
            numberOfRows: response.data.numberOfRows
          });
        } else {
          setPaginationInfo(prev => ({
            ...prev,
            numberOfRows: 0,
          }));
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setFullScreenLoader(false);
      }
    };
  
    useEffect(() => {
      fetchMembers();
    }, [searchValue, columnName, sortOrder, pageSize, page]);
  
    const handleEdit = (memberId: number) => {
      navigate(`/edit-member/${memberId}`);
    };
  
    const handlePageSizeChange = (newPageSize: number) => {
      setPage(1);
      setPageSize(newPageSize);
    };
  
    const handleSearch = (value: string) => {
      if (value !== searchValue) {
        setPage(1);
        setSearchValue(value);
      }
    };
  
    const handleSort = (column: string, direction: 'asc' | 'desc') => {
      setColumnName(column);
      setSortOrder(direction);
      setPage(1);
    };
  
    const confirmDelete = (member: Member) => {
      setMemberToDelete(member);
      toggleModal();
    };
  
    const handlePageChange = (newPage: number) => {
      setPage(newPage);
    };
  
    const toggleModal = () => {
      setModal(!modal);
    };
  
    const updateMemberStatus = async () => {
      if (memberToDelete) {
        toggleModal();
        setFullScreenLoader(true); 
        try {
          const response = await REQUEST_DELETE_MEMBERS_COMMON(memberToDelete.id);
  
          if (checkStatusCodeSuccess(response.statusCode)) {
            toast.success(response?.message);
            setMembers(members.filter(member => member.id !== memberToDelete.id));
          } else {
            throw new Error(response.message);
          }
        } catch (error: any) {
          toast.error(error.message);
        } finally {
          setFullScreenLoader(false); 
          setMemberToDelete(null);
        }
      }
    };
  
    document.title = PRODUCTS_TITLE;
  
    return (
      <div className="top-div">
        <div className="mx-4">
          <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
            <span className="fs-4 mx-2">{MEMBERS}</span>
            <BaseButton
              onClick={() => navigate("/add-member")}
              className="mt-0 w-auto px-4 py-2"
              color="info"
              text={<>
                <Icon path={mdiPlus} size={0.8} className="me-2"/>
                {ADD_LABEL}
              </>}
            />
          </div>

          <div className="card">
            <div className="card-body">
              <BaseTable
                columns={columns}
                data={members}
                isGlobalFilter={true}
                customPageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSearch={handleSearch}
                onSort={handleSort}
                tableClass="table bg-white"
                divClass="text-nowrap"
                manualPagination={true}
                tableLoader={loader}
                numberOfRows={paginationInfo.numberOfRows}
                currentPage={paginationInfo.currentPage}
                totalPages={paginationInfo.totalPages}
                totalRecords={paginationInfo.totalRecordsCount}
                SearchPlaceholder={SEARCH_PRODUCTS}
              />
            </div>
          </div>
        </div>

        <Modal
          isOpen={modal}
          toggle={toggleModal}
          className="modal-dialog-centered modal-md"
        >
          <ModalHeader toggle={toggleModal}>{DELETE_LABEL}</ModalHeader>
          <ModalBody>
            <p>{ARE_YOU_SURE_DELETE}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="success" onClick={updateMemberStatus}>
              {DELETE_LABEL}
            </Button>
            <Button color="danger" onClick={toggleModal}>
              {CANCEL_LABEL}
            </Button>
          </ModalFooter>
        </Modal>

        {fullScreenLoader && ( 
          <div className="full-screen-loader">
            <Spinner />
          </div>
        )}
      </div>
    );
}

export default ListOfMembers;
